import React from 'react';
import { Shield, AlertTriangle, AlertOctagon, TrendingUp, TrendingDown, Info } from 'lucide-react';

interface RiskScoreProps {
  score: string;
  totalFindings: number;
  openFindings: number;
  criticalRisks: number;
  highRisks: number;
}

const RiskScore: React.FC<RiskScoreProps> = ({
  score,
  totalFindings,
  openFindings,
  criticalRisks,
  highRisks,
}) => {
  const percentage = (openFindings / totalFindings) * 100;
  const circumference = 2 * Math.PI * 60;
  const strokeDashoffset = circumference * (1 - percentage / 100);

  const getScoreColor = (score: string) => {
    switch (score) {
      case 'A+': return {
        fill: 'fill-emerald-500',
        text: 'text-emerald-500',
        ring: 'ring-emerald-500/20',
        gradient: 'from-emerald-500/20',
        shadow: 'shadow-emerald-500/20',
        glow: 'after:bg-emerald-500/10',
        hex: '#50B680'
      };
      case 'A': return {
        fill: 'fill-green-500',
        text: 'text-green-500',
        ring: 'ring-green-500/20',
        gradient: 'from-green-500/20',
        shadow: 'shadow-green-500/20',
        glow: 'after:bg-green-500/10',
        hex: '#10B981'
      };
      case 'B': return {
        fill: 'fill-yellow-500',
        text: 'text-yellow-500',
        ring: 'ring-yellow-500/20',
        gradient: 'from-yellow-500/20',
        shadow: 'shadow-yellow-500/20',
        glow: 'after:bg-yellow-500/10',
        hex: '#EAB308'
      };
      case 'C': return {
        fill: 'fill-orange-500',
        text: 'text-orange-500',
        ring: 'ring-orange-500/20',
        gradient: 'from-orange-500/20',
        shadow: 'shadow-orange-500/20',
        glow: 'after:bg-orange-500/10',
        hex: '#F97316'
      };
      case 'D':
      case 'F': return {
        fill: 'fill-red-500',
        text: 'text-red-500',
        ring: 'ring-red-500/20',
        gradient: 'from-red-500/20',
        shadow: 'shadow-red-500/20',
        glow: 'after:bg-red-500/10',
        hex: '#EF4444'
      };
      default: return {
        fill: 'fill-gray-500',
        text: 'text-gray-500',
        ring: 'ring-gray-500/20',
        gradient: 'from-gray-500/20',
        shadow: 'shadow-gray-500/20',
        glow: 'after:bg-gray-500/10',
        hex: '#6B7280'
      };
    }
  };

  const scoreColors = getScoreColor(score);

  return (
    <div className="bg-[#0F1629] rounded-xl p-6 relative overflow-hidden group backdrop-blur-3xl border border-white/[0.05] hover:border-white/[0.1] transition-colors duration-700">
      {/* Ambient light effect */}
      <div className="absolute inset-0 bg-gradient-to-tr from-white/[0.01] via-transparent to-transparent opacity-50" />
      <div className={`absolute -inset-[150px] ${scoreColors.gradient} blur-[100px] opacity-20 group-hover:opacity-30 transition-opacity duration-700`} />
      
      {/* Animated gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${scoreColors.gradient} to-transparent opacity-0 group-hover:opacity-5 transition-all duration-700`} />
      
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full ${scoreColors.fill} opacity-20 animate-float`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-2">
            <div className={`p-2 rounded-lg bg-gray-800/50 ${scoreColors.text}`}>
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Risk Score</h2>
              <p className="text-xs text-gray-400">Updated every 5 minutes</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-gray-800/50">
              <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-sm text-gray-400">Live</span>
            </div>
            <button className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800/80 transition-colors">
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex items-center space-x-8">
          {/* Score Circle */}
          <div className="relative group/score">
            {/* Glow effect */}
            <div className={`absolute inset-0 rounded-full ${scoreColors.ring} blur-2xl transform group-hover/score:scale-150 transition-transform duration-500`} />
            
            {/* Score container */}
            <div
              className={`relative bg-gray-800/20 rounded-full p-2 backdrop-blur-sm border border-white/[0.05] group-hover/score:border-white/[0.1] transition-colors duration-700`}
              // onClick={() => { window.location.href = '/data-protection/public-leaks' }}
            >
              <svg className="w-32 h-32 transform transition-transform duration-500 group-hover/score:scale-105">
                {/* Background circle */}
                <circle
                  cx="64"
                  cy="64"
                  r="60"
                  className="fill-none stroke-gray-800/50"
                  strokeWidth="8"
                />
                {/* Progress circle with animation */}
                <circle
                  cx="64"
                  cy="64"
                  r="60"
                  className={`fill-none`}
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  transform="rotate(-90 64 64)"
                  style={{
                    color: score === 'A' ? '#5EC65F' : 
                           score === 'A+' ? '#50B680' :
                           score === 'B' ? '#EAB308' :
                           score === 'C' ? '#F97316' :
                           '#EF4444'
                  }}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={circumference}
                    to={strokeDashoffset}
                    dur="1.5s"
                    fill="freeze"
                    calcMode="spline"
                    keySplines="0.4 0 0.2 1"
                  />
                </circle>
                {/* Score text */}
                <text
                  x="64"
                  y="64"
                  className={`text-6xl font-bold ${scoreColors.fill}`}
                  dominantBaseline="middle"
                  textAnchor="middle"
                  style={{ filter: `drop-shadow(0 0 8px ${scoreColors.hex}40)` }}
                >
                  {score}
                </text>
              </svg>
              {/* Trend indicator with glass effect */}
              <div className={`absolute -right-2 -top-2 p-2 rounded-full bg-gray-800/80 backdrop-blur-sm border border-gray-700/50 ${scoreColors.text} transform transition-transform duration-300 group-hover/score:scale-110`}>
                {score <= 'C' ? (
                  <TrendingDown className="h-4 w-4 animate-bounce" />
                ) : (
                  <TrendingUp className="h-4 w-4 animate-bounce" />
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-6 flex-1">
            {/* Findings with animated progress */}
            <div className="bg-gray-800/20 rounded-lg p-4 border border-white/[0.02] hover:border-white/[0.1] transition-colors duration-700 cursor-pointer"
            onClick={() => { window.location.href = '/findings' }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm text-gray-400">Open Findings</span>
                  <div className="text-xs text-gray-500">Last 30 days</div>
                </div>
                <span className="text-lg font-medium text-white">
                  {openFindings} <span className="text-gray-500">/ {totalFindings}</span>
                </span>
              </div>
              <div className="h-2 bg-gray-800/50 rounded-full overflow-hidden backdrop-blur-sm">
                <div
                  className={`h-full ${scoreColors.fill} rounded-full transition-all duration-1000 ease-out relative`}
                  style={{ width: `${percentage}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 animate-shine" />
                </div>
              </div>
            </div>

            {/* Risk Breakdown */}
            <div className="space-y-3">
              {/* Critical Risks */}
              <div className="group/risk relative">
                <div className="absolute inset-0 bg-red-500/5 rounded-lg blur-sm opacity-0 group-hover/risk:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center justify-between p-4 bg-gray-800/20 hover:bg-gray-800/30 rounded-lg border border-red-500/10 transition-all duration-300 cursor-pointer"
                onClick={() => { window.location.href = '/findings?severity=critical' }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <AlertOctagon className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-red-500">Critical Risks</div>
                      <div className="text-xs text-red-500/70">Immediate action required</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-red-500">{criticalRisks}</span>
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
                  </div>
                </div>
              </div>

              {/* High Risks */}
              <div className="group/risk relative">
                <div className="absolute inset-0 bg-orange-500/5 rounded-lg blur-sm opacity-0 group-hover/risk:opacity-100 transition-opacity duration-300" />
                <div
                  className="relative flex items-center justify-between p-4 bg-gray-800/20 hover:bg-gray-800/30 rounded-lg border border-orange-500/10 transition-all duration-300 cursor-pointer"
                  onClick={() => { window.location.href = '/findings?severity=high' }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-orange-500/10">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-orange-500">High Risks</div>
                      <div className="text-xs text-orange-500/70">Action needed soon</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-orange-500">{highRisks}</span>
                    <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskScore;